import React, { CSSProperties, Fragment } from 'react';
import { Breakpoint } from 'react-socks';
import { common, query } from '@kurtosys/ksys-app-template';
import { IComponentStyle } from '@kurtosys/types/componentStyle/IComponentStyle.js';
import { GlobalDisclaimerLevel } from '@kurtosys/types/disclaimers/GlobalDisclaimerLevel.js';
import { ITargettedDisclaimers } from '@kurtosys/types/disclaimers/ITargettedDisclaimers.js';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { IConditionalClassName } from '../../../models/IConditionalClassName.js';
import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { QueryStore } from '../../../shared/QueryStore.js';
import { IDisclaimerProps } from '../../overview/Disclaimer/models/index.js';
import Disclaimers from '../../overview/Disclaimers/Disclaimers.js';
import { IDisclaimersProps } from '../../overview/Disclaimers/models/index.js';
import AccordionPanel from '../AccordionPanel/AccordionPanel.js';
import List from '../List/List.js';
import { IListProps } from '../List/models/index.js';
import { ISortButtonProps, SortButtonDirection } from '../SortButton/models/index.js';
import Superscript from '../Superscript/Superscript.js';
import Tooltip from '../Tooltip/Tooltip.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import Translate from '../Translate/Translate.js';
import { TranslationContext } from '../Translate/Translate.js';

import Body from './components/Body/index.js';
import CardGrouping from './components/CardGrouping/index.js';
import CellValue from './components/CellValue/index.js';
import Head from './components/Head/index.js';
import {
	ITableConfiguration,
	ITableEvents,
	ITableEventsElement,
	ITableProps,
	ITableSort,
	ITableState,
	ITableTooltip,
	TableColumnDisplayType,
} from './models/index.js';
import { ITableAriaPagination } from './models/ITableAriaPagination.js';
import { ITableColumn } from './models/ITableColumn.js';
import { ITableGroup } from './models/ITableGroup.js';
import { ITableHeaderGroup } from './models/ITableHeaderGroup.js';
import { ITableRow } from './models/ITableRow.js';
import Caption from './styledComponents/Caption.js';
import Card from './styledComponents/Card.js';
import DisclaimersWrapper from './styledComponents/DisclaimersWrapper.js';
import ListGroup from './styledComponents/ListGroup.js';
import ListGroupSubTitle from './styledComponents/ListGroupSubTitle.js';
import ListGroupTitle from './styledComponents/ListGroupTitle.js';
import TableElement from './styledComponents/Table.js';
import Wrapper from './styledComponents/Wrapper.js';
import { TABLE_CATEGORY_KEY, TABLE_COMPONENT_KEY } from './Table.meta.js';
import { TableLogic } from './TableLogic.js';
import { TableLogicBase } from './TableLogicBase.js';

export class Table<TRow extends ITableRow> extends ComponentBase<ITableProps<TRow>, ITableState, ITableConfiguration> {
	categoryKey = TABLE_CATEGORY_KEY;
	componentKey = TABLE_COMPONENT_KEY;
	translationContext: ITranslationContextValue | undefined;
	static defaultProps: ITableProps = {
		cellPadding: 0,
		cellSpacing: 0,
	};

	static addSuperscriptDisclaimers(
		appKey: string,
		superscripts: query.disclaimers.GlobalDisclaimerStoreValue[],
		targettedDisclaimers: ITargettedDisclaimers,
		conditionalContext: any,
		responseContext: any,
		globalDisclaimerStore: query.stores.GlobalDisclaimerStore,
		queryStore: QueryStore,
	) {
		const { disclaimers, conditional } = targettedDisclaimers;
		if (conditional) {
			const conditionalHelper = new common.helpers.ConditionalHelper({ queryClass: query.Query }, conditional);
			if (
				conditionalHelper.matchesWithOptions({
					instance: conditionalContext,
					executionOptions: { responseContext: conditionalContext },
				})
			) {
				superscripts.push(...globalDisclaimerStore.add(appKey, responseContext, queryStore, ...disclaimers));
			}
		} else {
			superscripts.push(...globalDisclaimerStore.add(appKey, responseContext, queryStore, ...disclaimers));
		}
	}

	static loadSuperscripts = (
		appKey: string,
		columns: ITableColumn[],
		rows: ITableRow[],
		headerGroups: ITableHeaderGroup[],
		queryStore: QueryStore | undefined,
	) => {
		query.queryUtils.globalDisclaimers.initializeGlobalDisclaimers();
		const globalDisclaimerStore = query.queryUtils.globalDisclaimers.getGlobalDisclaimerStore();
		// To maintain the correct order of superscripts loaded, ie, numbering should be in sequence following the natural reading flow,
		// by increasing in value going left to right through the rows then columns.
		// Due to this, we will first need to load the header grouping, then headers, then lastly the rows.
		if (queryStore && globalDisclaimerStore) {
			Table.loadHeaderSuperscripts(appKey, columns, headerGroups, queryStore, globalDisclaimerStore);
			Table.loadCellSuperscripts(appKey, columns, rows, queryStore, globalDisclaimerStore);
		}
	};

	static loadHeaderSuperscripts = (
		appKey: string,
		columns: ITableColumn[],
		headerGroups: ITableHeaderGroup[],
		queryStore: QueryStore,
		globalDisclaimerStore: query.stores.GlobalDisclaimerStore,
	) => {
		// Load the header groups if exists, the app should pass in an empty array if not configured
		for (const headerGroup of headerGroups) {
			const { disclaimers: groupHeaderDisclaimers } = headerGroup;
			if (groupHeaderDisclaimers) {
				const groupHeaderSuperscripts: query.disclaimers.GlobalDisclaimerStoreValue[] = [];
				for (const groupHeaderDisclaimer of groupHeaderDisclaimers) {
					// Setup the data context for the conditional, so that users can conditionally display based on the header label
					const conditionalContext = { label: headerGroup.header };
					Table.addSuperscriptDisclaimers(
						appKey,
						groupHeaderSuperscripts,
						groupHeaderDisclaimer,
						conditionalContext,
						headerGroup,
						globalDisclaimerStore,
						queryStore,
					);
				}
				if (groupHeaderSuperscripts.length > 0) {
					if (!headerGroup._headerSuperscripts) {
						headerGroup._headerSuperscripts = [];
					}
					headerGroup._headerSuperscripts = groupHeaderSuperscripts
						.map((groupHeaderSuperscript) => groupHeaderSuperscript.superscript)
						.sort();
				}
			}
		}
		// Then load into the header cells on the column config
		for (const column of columns) {
			const columnHeaderDisclaimers: query.disclaimers.GlobalDisclaimerStoreValue[] = [];
			const { disclaimers } = column;
			const headerDisclaimers = disclaimers && disclaimers.headCell;
			if (headerDisclaimers) {
				for (const headerDisclaimer of headerDisclaimers) {
					const conditionalContext = { label: column.header };
					Table.addSuperscriptDisclaimers(
						appKey,
						columnHeaderDisclaimers,
						headerDisclaimer,
						conditionalContext,
						column,
						globalDisclaimerStore,
						queryStore,
					);
				}
			}
			if (columnHeaderDisclaimers.length > 0) {
				if (!column._headerSuperscripts) {
					column._headerSuperscripts = [];
				}
				column._headerSuperscripts = columnHeaderDisclaimers
					.map((columnHeaderDisclaimer) => columnHeaderDisclaimer.superscript)
					.sort();
			}
		}
	};

	static loadCellSuperscripts = (
		appKey: string,
		columns: ITableColumn[],
		rows: ITableRow[],
		queryStore: QueryStore,
		globalDisclaimerStore: query.stores.GlobalDisclaimerStore,
	) => {
		for (const row of rows) {
			for (const column of columns) {
				const columnDisclaimers: query.disclaimers.GlobalDisclaimerStoreValue[] = [];
				const { disclaimers: allDisclaimers } = column;
				const targettedDisclaimers = allDisclaimers && allDisclaimers.cell;
				if (targettedDisclaimers) {
					for (const targettedDisclaimer of targettedDisclaimers) {
						Table.addSuperscriptDisclaimers(
							appKey,
							columnDisclaimers,
							targettedDisclaimer,
							row,
							row,
							globalDisclaimerStore,
							queryStore,
						);
					}
				}
				if (columnDisclaimers && columnDisclaimers.length > 0) {
					const { id } = column;
					if (!row._superscriptsByColumnId) {
						row._superscriptsByColumnId = {};
					}
					row._superscriptsByColumnId[id] = columnDisclaimers
						.map((columnDisclaimer) => columnDisclaimer.superscript)
						.sort();
				}
			}
		}
	};

	public logic: TableLogic;
	public grouping: ITableGroup | null = null;
	public rawRows: TRow[] = [];
	public filteredRows: TRow[] = [];
	public rows: TRow[] = [];

	constructor(props: ITableProps<TRow>) {
		super(props);

		this.logic = new TableLogic(props, this.globalConfiguration);
		this.grouping = this.logic.getGrouping(props);
		this.initRows(props);

		makeObservable<Table<TRow>>(this, {
			sort: computed,
			ariaPagination: computed,
			ariaStartingRowIndex: computed,
			ariaRowCount: computed,
			sortingDirections: observable.ref,
			wrapSort: action,
			grouping: observable.ref,
			hasGrouping: computed,
			rows: observable.ref,
			rowSelectionChange: computed,
			emptyColumnIds: computed,
			columns: computed,
			headerColumns: computed,
			events: computed,
		});
	}

	private initRows(props: ITableProps<TRow>) {
		const rows = props.rows || [];
		this.rawRows = [...rows.map((r) => structuredClone(r))];
		this.filteredRows = this.logic.filterRowsByGrouping(this.rawRows);
		this.rows = this.logic.getRowsWithExpanders({ rows: this.filteredRows, grouping: props.grouping });
	}
	
	public get hasGrouping(): boolean {
		return !!this.grouping;
	}

	get sort(): ITableSort {
		const defaultSort: ITableSort = {
			mode: 'multiple',
		};
		return {
			...defaultSort,
			...(this.props.sort || {}),
		};
	}

	get ariaPagination(): ITableAriaPagination | undefined {
		return this.props.ariaPagination;
	}

	get ariaStartingRowIndex(): number | undefined {
		if (this.ariaPagination) {
			return this.ariaPagination.startingRecord;
		}
	}

	get ariaRowCount(): number {
		if (this.ariaPagination) {
			return this.ariaPagination.totalRecords;
		}
		return -1;
	}

	sortingDirections: { [key: string]: ISortButtonProps } = {};

	wrapSort = (onChangeFn?: (payload: any, direction: SortButtonDirection) => Promise<void>) => {
		const response = async (payload: any, direction: SortButtonDirection) => {
			if (this.sort.mode === 'single') {
				Object.keys(this.sortingDirections).forEach((key) => {
					this.sortingDirections[key] = {
						direction: SortButtonDirection.unset,
					};
				});
				this.sortingDirections[payload.id] = {
					direction,
				};
				this.sortingDirections = { ...this.sortingDirections };
			}
			if (onChangeFn) {
				onChangeFn(payload, direction);
			}
		};
		return response;
	};

	componentDidUpdate(prevProps: ITableProps<TRow>) {
		if (
			common.commonUtils.stringifySafe(prevProps.grouping || {}) !==
				common.commonUtils.stringifySafe(this.props.grouping || {}) ||
			(common.commonUtils.stringifySafe(prevProps.columns || []) !==
				common.commonUtils.stringifySafe(this.props.columns || []) &&
				this.props.grouping)
		) {
			this.logic = new TableLogic(this.props, this.globalConfiguration);
			this.grouping = this.logic.getGrouping(this.props);
			this.forceUpdate();
		}
		const currentRows = this.rawRows;
		const newRows = this.props.rows || [];

		const updateRows = () => {
			this.logic = new TableLogic(this.props, this.globalConfiguration);
			this.initRows(this.props);
			this.forceUpdate();
		};

		if (currentRows.length === newRows.length) {
			const comparisonRows = [];
			// If a row key value is being explicitly mutated, add it to the keysToIgnore to exclude it from the row comparison check
			const keysToIgnore = ['_rowKey', '_expanded'];
			for (let i = 0; i < currentRows.length; i++) {
				const comparisonRow = { ...currentRows[i] };
				const newRow = newRows[i];
				let mustBreak = false;
				keysToIgnore.forEach((keyToIgnore) => {
					// When we have the absence of the key in the object vs having the key but it's set to false. Basically creates a !falsePositive
					if (!newRow[keyToIgnore] && comparisonRow[keyToIgnore]) {
						delete comparisonRow[keyToIgnore];
					} else if (newRow[keyToIgnore] !== comparisonRow[keyToIgnore]) {
						mustBreak = true;
					}
				});
				if (mustBreak) {
					break;
				}
				comparisonRows.push(comparisonRow);
			}
			if (comparisonRows.length === newRows.length) {
				if (
					common.commonUtils.stringifySafe(comparisonRows || []) !==
					common.commonUtils.stringifySafe(newRows || [])
				) {
					updateRows();
				}
			} else {
				updateRows();
			}
		} else {
			updateRows();
		}
	}

	public get rowSelectionChange() {
		return this.props.rowSelectionChange;
	}

	get emptyColumnIds(): string[] {
		return this.logic.emptyColumnIds;
	}

	get columns(): ITableColumn[] {
		return this.logic.visibleColumns;
	}

	get headerColumns(): ITableColumn[] {
		return this.logic.headerColumns;
	}

	get events(): ITableEvents {
		return this.props.events || {};
	}
	getTooltipHeaderProps(
		column: ITableColumn | ITableHeaderGroup,
		matchingProperties: Partial<ITableTooltip>,
	): ITableTooltip | undefined {
		const { queryStore } = this.props;
		let response;
		if (column && Array.isArray(column.tooltips) && column.tooltips.length > 0) {
			const tooltips = common.commonUtils.filterWithMatchingProperties(column.tooltips, matchingProperties);
			if (tooltips) {
				for (let i = 0; i < tooltips.length; i++) {
					const tooltip = tooltips[i];
					if (tooltip && tooltip.conditional && tooltip.conditional && queryStore) {
						const conditionalTooltipHelper = new common.helpers.ConditionalHelper(
							{ queryClass: query.Query },
							tooltip.conditional,
						);
						if (
							conditionalTooltipHelper.matchesWithOptions({
								executionOptions: queryStore.executionOptions,
							})
						) {
							response = tooltip;
							break;
						}
					} else if (tooltip) {
						response = tooltip;
						break;
					}
				}
			}
		}
		return response;
	}
	getTooltipRowProps = TableLogicBase.getTooltipRowProps;

	getTooltips(elements: ITableTooltip[]) {
		const response: JSX.Element[] = [];
		for (let index = 0; index < elements.length; index++) {
			const element = elements[index];
			response.push(this.getTooltip(element, `tooltip_${index}`));
		}
		return response;
	}
	getTooltip(element: ITableTooltip, key?: string) {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { target, conditional, ...tooltipProps } = element;
		return tooltipProps && <Tooltip key={key} {...tooltipProps} />;
	}
	getEvents<T extends HTMLElement>(
		eventsForElement: ITableEventsElement<T> = {},
		row?: any,
		rowIndex?: number,
		column?: ITableColumn | ITableHeaderGroup,
		columnIndex?: number,
		cellValue?: any,
	) {
		const response: React.DOMAttributes<T> = {};
		const keys = Object.keys(eventsForElement) as (keyof ITableEventsElement<T>)[];
		for (const eventKey of keys) {
			const handler = eventsForElement[eventKey];
			response[eventKey] = (event) => handler && handler(event, row, rowIndex, column, columnIndex, cellValue);
		}
		return response;
	}
	getConditionalClassName = (
		responseContext: any,
		conditionalClassNames?: IConditionalClassName[],
		baseClassName?: string,
	) => {
		const { queryStore } = this.props;
		const classNames: string[] = [];
		if (baseClassName) {
			classNames.push(baseClassName);
		}
		if (conditionalClassNames) {
			for (const conditionalClassName of conditionalClassNames) {
				const { className, classNameQuery: classNameQueryOptions, conditional } = conditionalClassName;
				let mustAddClassName = false;
				if (conditional) {
					if (queryStore) {
						const conditionalHelper = new common.helpers.ConditionalHelper(
							{ queryClass: query.Query },
							conditional,
						);
						mustAddClassName = conditionalHelper.matchesWithOptions({
							executionOptions: {
								...queryStore.executionOptions,
								responseContext,
							},
						});
					}
				} else {
					mustAddClassName = true;
				}
				if (mustAddClassName) {
					if (className) {
						classNames.push(className);
					} else if (classNameQueryOptions) {
						if (queryStore) {
							const queryResponse = queryStore.query(classNameQueryOptions, { responseContext });
							if (typeof queryResponse === 'string') {
								classNames.push(queryResponse);
							}
						}
					}
				}
			}
		}
		return classNames.join(' ');
	};

	getRowKey(row: ITableRow, rowIndex: number) {
		if (row && row['_rowKey']) {
			return row['_rowKey'];
		}
		return `row_${rowIndex}`;
	}

	orchestrateComponentStyle = (componentStyleProps: any, initialStyle?: IComponentStyle, styleQueries?: any) => {
		let componentStyle: IComponentStyle | undefined = initialStyle;
		const { queryStore } = this.props;
		if (queryStore && styleQueries) {
			componentStyle = queryStore.mergeQueriesAndProps(
				{
					props: initialStyle || {},
					queries: styleQueries,
				},
				componentStyleProps,
			);
		}
		return componentStyle;
	};

	getCardGroupingStyleProps(row: ITableRow, rowIndex: number, grouping?: ITableGroup) {
		const componentStyleProps = {
			row,
			rowIndex,
		};
		const { cardGroupingStyle, cardGroupingStyleQueries, cardGroupingClasses } = this.props;
		let className = this.getConditionalClassName(componentStyleProps, cardGroupingClasses);
		let componentStyle = this.orchestrateComponentStyle(
			componentStyleProps,
			cardGroupingStyle,
			cardGroupingStyleQueries,
		);
		if (grouping) {
			const {
				cardGroupingStyle: groupCardGroupingStyle,
				cardGroupingStyleQueries: groupCardGroupingStyleQueries,
				cardGroupingClasses: groupCardGroupingClasses,
			} = grouping;
			if (groupCardGroupingClasses && groupCardGroupingClasses.length > 0) {
				className = this.getConditionalClassName(componentStyleProps, groupCardGroupingClasses, className);
			}
			if (groupCardGroupingStyle || groupCardGroupingStyleQueries) {
				const groupComponentStyle = this.orchestrateComponentStyle(
					componentStyleProps,
					groupCardGroupingStyle,
					groupCardGroupingStyleQueries,
				);
				componentStyle = common.commonUtils.deepMergeObjects(componentStyle, groupComponentStyle);
			}
		}
		return {
			className,
			componentStyleProps,
			componentStyle,
		};
	}

	groupingResponse = (rows: TRow[], grouping: ITableGroup) => {
		return rows.map((row, rowIndex) => {
			const key = this.getRowKey(row, rowIndex);
			const cardGroupingStyleProps = this.getCardGroupingStyleProps(row, rowIndex, grouping);
			const { suppressAnalytics, analyticsContext } = this.props;

			return (
				<CardGrouping
					{...cardGroupingStyleProps}
					key={key}
					table={this}
					row={row}
					rowIndex={rowIndex}
					grouping={grouping}
					suppressAnalytics={suppressAnalytics}
					analyticsContext={analyticsContext}
				/>
			);
		});
	};

	renderCards = (rows: TRow[], grouping?: ITableGroup | null) => {
		if (grouping && grouping.getRowsQuery) {
			return this.groupingResponse(rows, grouping);
		}
		const response: React.ReactNode[] = rows.map((row, rowIndex) => {
			const events = this.getEvents(this.events && this.events['card'], row, rowIndex);
			return (
				<Card key={rowIndex} {...events}>
					{this.renderListForRow(row)}
					{grouping &&
						grouping.fetchChildren &&
						grouping.id !== '_lastGroup' &&
						this.groupingResponse([row], grouping)}
				</Card>
			);
		});
		return response;
	};

	isPanelOpen(heading: string, index: number) {
		const { accordionProps } = this.props;
		const openPanels = (accordionProps && accordionProps.openPanels) || [];
		return openPanels.indexOf(heading) >= 0 || openPanels.indexOf(index) >= 0;
	}

	renderAccordion = () => {
		let headingKey = 'name';
		if (!common.commonUtils.isNullOrEmpty(this.columns)) {
			headingKey = this.columns[0].key;
		}

		const response: React.ReactNode[] = this.rows.map((row, rowIndex) => {
			const key = `panel_${rowIndex}`;
			const heading = (row as any)[headingKey];
			const isOpen = this.isPanelOpen(heading, rowIndex);
			return (
				<AccordionPanel key={key} panelKey={key} heading={heading} isOpen={isOpen}>
					{this.renderListForRow(row, false)}
				</AccordionPanel>
			);
		});
		return response;
	};

	renderListForRow = (row: TRow, includeHeader = true) => {
		const { cardListProps = {}, displayResponsiveGroups, headerGroups = [] } = this.props;
		const slicedColumns = this.columns.slice(includeHeader ? 0 : 1);
		let lastHeaderGroup = headerGroups[0];

		const rowColumnIdentifier = slicedColumns.find((column) => column.isRowIdentifier);
		let rowIdentifier: string | undefined;
		if (rowColumnIdentifier) {
			rowIdentifier = `${row._rowKey}_${rowColumnIdentifier.id}`;
		}

		const items = slicedColumns
			.filter((column) => !column.hideOnMobile)
			.reduce((listItems, column) => {
				const headerGroup = headerGroups.find((group) => group.id === column.headerGroupId);

				const columnHeaderTooltip = this.getTooltipHeaderProps(column, { target: 'header' });
				const columnValueTooltips = this.getTooltipRowProps(row, column);

				if (displayResponsiveGroups && headerGroup && headerGroup !== lastHeaderGroup) {
					const groupHeaderTooltip = this.getTooltipHeaderProps(headerGroup, { target: 'header' });
					const groupHeaderSubTitleTooltip = this.getTooltipHeaderProps(headerGroup, { target: 'subtitle' });
					const groupLabel = (
						<ListGroup>
							<ListGroupTitle>
								<Translate>{headerGroup.header}</Translate>
								{groupHeaderTooltip && this.getTooltip(groupHeaderTooltip)}{' '}
								<ListGroupSubTitle>
									<Translate>{headerGroup.subTitle}</Translate>
									{groupHeaderSubTitleTooltip && this.getTooltip(groupHeaderSubTitleTooltip)}
								</ListGroupSubTitle>
							</ListGroupTitle>
						</ListGroup>
					);
					listItems.push([groupLabel]);
					lastHeaderGroup = headerGroup;
				}

				const labelText = column.header;
				const label = (
					<React.Fragment>
						<Translate>{column.header}</Translate>
						{columnHeaderTooltip && this.getTooltip(columnHeaderTooltip)}
						{column._headerSuperscripts && (
							<Superscript>{column._headerSuperscripts.join(',')}</Superscript>
						)}
					</React.Fragment>
				);
				// only set the aria-describedby if the column is not the identifier
				const cellDescribedby = column.isRowIdentifier === false ? rowIdentifier : undefined;
				const valueText = this.logic.getValue(row, column, undefined, undefined, this, cellDescribedby);
				const cellSuperscripts = this.logic.getCellSuperscripts(column, row);
				const shouldRenderCellSuperscripts =
					!column.changeIndicatorProps &&
					(!column.display || column.display.type !== TableColumnDisplayType.changeIndicator);

				const value = (
					<React.Fragment>
						<CellValue table={this} cellValue={valueText} column={column} />
						<React.Fragment>{columnValueTooltips && this.getTooltips(columnValueTooltips)}</React.Fragment>
						<React.Fragment>
							{shouldRenderCellSuperscripts && cellSuperscripts && (
								<Superscript>{cellSuperscripts.join(',')}</Superscript>
							)}
						</React.Fragment>
					</React.Fragment>
				);
				const item = [label, value].filter((field) => !common.commonUtils.isNullOrUndefined(field));
				if (
					item &&
					item.length > 0 &&
					(!common.commonUtils.isNullOrWhitespace(labelText as any) ||
						column.contextMenuProps ||
						column.checkboxProps ||
						(!common.commonUtils.isNullOrUndefined(valueText) &&
							(typeof valueText !== 'string' || !common.commonUtils.isNullOrWhitespace(valueText))))
				) {
					listItems.push(item);
				}

				return listItems;
			}, [] as any[]);

		const listProps: IListProps = common.commonUtils.deepMergeObjects(cardListProps, { items });
		return <List {...listProps} />;
	};
	renderTable = () => {
		const {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			className,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			columns,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			rows,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			disclaimersProps,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			responsiveMode,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			cardListProps,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			formatsByType,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			excludeEmptyColumns,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			conditionalStyles,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars,
			conditionalHeadStyles,

			describedby,
			caption,
			suppressAnalytics,
			analyticsContext,
			// tslint:disable-next-line: trailing-comma
			...otherProps
		} = this.props;
		const events = this.getEvents(this.events.table);
		const { stickyProps } = otherProps;
		const headStickyStyles: CSSProperties =
			(stickyProps && { position: 'sticky', top: stickyProps.headerTop, zIndex: stickyProps.headerZIndex }) || {};
		return (
			<TableElement
				aria-describedby={describedby}
				aria-rowcount={this.ariaRowCount}
				{...(events || {})}
				{...otherProps}
			>
				<Caption>
					<Translate>{caption}</Translate>
				</Caption>
				<Head style={headStickyStyles} table={this} headerRows={this.logic.headerMapping} />
				<Body
					stickyProps={stickyProps}
					table={this}
					rows={this.rows}
					suppressAnalytics={suppressAnalytics}
					analyticsContext={analyticsContext}
				/>
			</TableElement>
		);
	};
	getDisclaimersProps = (): IDisclaimersProps | undefined => {
		const { disclaimersProps: explicitDisclaimersProps } = this.props;
		const response: IDisclaimersProps = JSON.parse(
			common.commonUtils.stringifySafe(explicitDisclaimersProps || {}),
		);
		const globalDisclaimersStore = query.queryUtils.globalDisclaimers.getGlobalDisclaimerStore();
		if (globalDisclaimersStore) {
			const appKey = this.props.configuration && this.props.configuration.appKey;
			if (appKey) {
				const globalDisclaimersForApp = globalDisclaimersStore.get(GlobalDisclaimerLevel.app, appKey);
				if (globalDisclaimersForApp && globalDisclaimersForApp.length > 0) {
					if (!response.disclaimers) {
						response.disclaimers = [];
					}
					response.disclaimers.push(
						...globalDisclaimersForApp.map((globalDisclaimerForApp) => {
							const p: IDisclaimerProps = {
								text: globalDisclaimerForApp.text,
								superscript:
									typeof globalDisclaimerForApp.superscript === 'number'
										? globalDisclaimerForApp.superscript.toString()
										: globalDisclaimerForApp.superscript,
							};
							return p;
						}),
					);
				}
			}
		}
		return response;
	};
	render() {
		const { className, hideDisclaimers, responsiveMode = 'renderTable' } = this.props;
		const disclaimersProps = this.getDisclaimersProps();
		return (
			<Wrapper className={className}>
				<TranslationContext.Consumer>
					{(translationContext: ITranslationContextValue) => {
						this.translationContext = translationContext;
						return (
							<Fragment>
								<Breakpoint small down>
									{responsiveMode === 'pivotToCard' && this.renderCards(this.rows, this.grouping)}
									{responsiveMode === 'pivotToAccordion' && this.renderAccordion()}
									{(!responsiveMode || responsiveMode === 'renderTable') && this.renderTable()}
								</Breakpoint>
								<Breakpoint medium up>
									{this.renderTable()}
								</Breakpoint>
								{!hideDisclaimers && disclaimersProps && (
									<DisclaimersWrapper>
										<Disclaimers {...disclaimersProps} />
									</DisclaimersWrapper>
								)}
							</Fragment>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Table), TABLE_CATEGORY_KEY, TABLE_COMPONENT_KEY);
